import gql from "graphql-tag";

export default (config) => {
  const { client } = config;

  return {
    /**
     * Get aggregated monthly sales data
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Array>}
     */
    getMonthlyAggregation: async (filters = {}) => {
      const query = gql`
        query GetMonthlySalesAgg(
          $where: reporting_sol_agg_monthly_bool_exp
          $order_by: [reporting_sol_agg_monthly_order_by!]
        ) {
          reporting_sol_agg_monthly(where: $where, order_by: $order_by) {
            period_start
            customer_account_id
            top_group_id
            status
            qty
            revenue
            line_count
            qty_vs_prev_month
            revenue_vs_prev_month
            qty_vs_same_month_last_year
            revenue_vs_same_month_last_year
            account {
              id
              company_name
            }
            item_group {
              id
              code
              description
            }
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: {
          where: filters.where || {},
          order_by: filters.order_by || { period_start: "asc" },
        },
        fetchPolicy: "network-only",
      });

      return data.reporting_sol_agg_monthly;
    },

    /**
     * Get aggregated quarterly sales data
     */
    getQuarterlyAggregation: async (filters = {}) => {
      const query = gql`
        query GetQuarterlySalesAgg(
          $where: reporting_sol_agg_quarterly_bool_exp
          $order_by: [reporting_sol_agg_quarterly_order_by!]
        ) {
          reporting_sol_agg_quarterly(where: $where, order_by: $order_by) {
            period_start
            customer_account_id
            top_group_id
            status
            qty
            revenue
            line_count
            qty_vs_prev_quarter
            revenue_vs_prev_quarter
            qty_vs_same_quarter_last_year
            revenue_vs_same_quarter_last_year
            account {
              id
              company_name
            }
            item_group {
              id
              code
              description
            }
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: {
          where: filters.where || {},
          order_by: filters.order_by || { period_start: "asc" },
        },
        fetchPolicy: "network-only",
      });

      return data.reporting_sol_agg_quarterly;
    },

    /**
     * Get aggregated yearly sales data
     */
    getYearlyAggregation: async (filters = {}) => {
      const query = gql`
        query GetYearlySalesAgg(
          $where: reporting_sol_agg_yearly_bool_exp
          $order_by: [reporting_sol_agg_yearly_order_by!]
        ) {
          reporting_sol_agg_yearly(where: $where, order_by: $order_by) {
            period_start
            customer_account_id
            top_group_id
            status
            qty
            revenue
            line_count
            qty_vs_prev_year
            revenue_vs_prev_year
            account {
              id
              company_name
            }
            item_group {
              id
              code
              description
            }
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: {
          where: filters.where || {},
          order_by: filters.order_by || { period_start: "asc" },
        },
        fetchPolicy: "network-only",
      });

      return data.reporting_sol_agg_yearly;
    },

    /**
     * Get aggregated weekly sales data
     */
    getWeeklyAggregation: async (filters = {}) => {
      const query = gql`
        query GetWeeklySalesAgg(
          $where: reporting_sol_agg_weekly_bool_exp
          $order_by: [reporting_sol_agg_weekly_order_by!]
        ) {
          reporting_sol_agg_weekly(where: $where, order_by: $order_by) {
            period_start
            customer_account_id
            top_group_id
            status
            qty
            revenue
            line_count
            qty_vs_prev_week
            revenue_vs_prev_week
            qty_vs_same_week_last_year
            revenue_vs_same_week_last_year
            account {
              id
              company_name
            }
            item_group {
              id
              code
              description
            }
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: {
          where: filters.where || {},
          order_by: filters.order_by || { period_start: "asc" },
        },
        fetchPolicy: "network-only",
      });

      return data.reporting_sol_agg_weekly;
    },

    /**
     * Get comprehensive sales report using the main function
     */
    getMetricsReport: async (params) => {
      const query = gql`
        query GetSalesMetricsReport(
          $p_start_date: date!
          $p_end_date: date!
          $p_customer_ids: _uuid
          $p_top_group_ids: _uuid
          $p_statuses: _text
          $p_tags: _text
          $p_tag_mode: String
        ) {
          reporting_get_sol_metrics_report(
            args: {
              p_start_date: $p_start_date
              p_end_date: $p_end_date
              p_customer_ids: $p_customer_ids
              p_top_group_ids: $p_top_group_ids
              p_statuses: $p_statuses
              p_tags: $p_tags
              p_tag_mode: $p_tag_mode
            }
          ) {
            status_date
            customer_account_id
            top_group_id
            status
            qty
            revenue
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: params,
        fetchPolicy: "network-only",
      });

      // The function now returns raw facts, we need to aggregate them by period
      return data.reporting_get_sol_metrics_report;
    },

    /**
     * Get sales metrics by tags (ANY mode)
     */
    getMetricsByTagsAny: async (params) => {
      const query = gql`
        query GetMetricsByTagsAny(
          $p_start_date: date!
          $p_end_date: date!
          $p_tags: _text!
          $p_granularity: String
          $p_customer_ids: _uuid
          $p_statuses: _text
        ) {
          reporting_get_sol_metrics_by_tags_any(
            args: {
              p_start_date: $p_start_date
              p_end_date: $p_end_date
              p_tags: $p_tags
              p_granularity: $p_granularity
              p_customer_ids: $p_customer_ids
              p_statuses: $p_statuses
            }
          ) {
            period_start
            customer_account_id
            top_group_id
            status
            qty
            revenue
            line_count
            matched_tags
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: params,
        fetchPolicy: "network-only",
      });

      return data.reporting_get_sol_metrics_by_tags_any;
    },

    /**
     * Get sales metrics by tags (ALL mode)
     */
    getMetricsByTagsAll: async (params) => {
      const query = gql`
        query GetMetricsByTagsAll(
          $p_start_date: date!
          $p_end_date: date!
          $p_tags: _text!
          $p_granularity: String
          $p_customer_ids: _uuid
          $p_statuses: _text
        ) {
          reporting_get_sol_metrics_by_tags_all(
            args: {
              p_start_date: $p_start_date
              p_end_date: $p_end_date
              p_tags: $p_tags
              p_granularity: $p_granularity
              p_customer_ids: $p_customer_ids
              p_statuses: $p_statuses
            }
          ) {
            period_start
            customer_account_id
            top_group_id
            status
            qty
            revenue
            line_count
            matched_tags
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: params,
        fetchPolicy: "network-only",
      });

      return data.reporting_get_sol_metrics_by_tags_all;
    },

    /**
     * Get tag breakdown report
     */
    getTagBreakdown: async (params) => {
      const query = gql`
        query GetTagBreakdown(
          $p_start_date: date!
          $p_end_date: date!
          $p_tags: _text!
          $p_granularity: String
          $p_customer_ids: _uuid
          $p_statuses: _text
        ) {
          reporting_get_sol_metrics_by_tag_breakdown(
            args: {
              p_start_date: $p_start_date
              p_end_date: $p_end_date
              p_tags: $p_tags
              p_granularity: $p_granularity
              p_customer_ids: $p_customer_ids
              p_statuses: $p_statuses
            }
          ) {
            period_start
            customer_account_id
            top_group_id
            status
            tag
            qty
            revenue
            line_count
          }
        }
      `;

      const { data } = await client.query({
        query,
        variables: params,
        fetchPolicy: "network-only",
      });

      return data.reporting_get_sol_metrics_by_tag_breakdown;
    },

    /**
     * Get customers for dropdown
     */
    getCustomers: async () => {
      const query = gql`
        query GetCustomers {
          account(
            where: { is_customer: { _eq: true } }
            order_by: [{ rating: desc }, { company_name: asc }]
          ) {
            id
            company_name
            rating
          }
        }
      `;

      const { data } = await client.query({
        query,
        fetchPolicy: "cache-first",
      });

      return data.account;
    },

    /**
     * Get top-level item groups for dropdown
     */
    getTopItemGroups: async () => {
      const query = gql`
        query GetTopItemGroups {
          item_group(
            where: { parent_id: { _is_null: true } }
            order_by: { code: asc }
          ) {
            id
            code
            description
          }
        }
      `;

      const { data } = await client.query({
        query,
        fetchPolicy: "cache-first",
      });

      return data.item_group;
    },

    /**
     * Get unique tags from items
     */
    getUniqueTags: async () => {
      const query = gql`
        query GetUniqueTags {
          item(where: { tags: { _neq: "[]" } }, distinct_on: tags) {
            tags
          }
        }
      `;

      const { data } = await client.query({
        query,
        fetchPolicy: "cache-first",
      });

      // Extract unique tags from JSONB arrays
      const tagsSet = new Set();
      data.item.forEach((item) => {
        if (item.tags && Array.isArray(item.tags)) {
          item.tags.forEach((tag) => tagsSet.add(tag));
        }
      });

      return Array.from(tagsSet).sort();
    },
  };
};
